import { formatCount } from "../../lib/format";

/** "Mon, Oct 12" for a UTC day key ("2026-10-12"); reporting days are UTC everywhere (docs/07 section 7). */
export function utcDayLabel(day: string, weekday = true): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-US", { ...(weekday ? { weekday: "short" as const } : {}), month: "short", day: "numeric", timeZone: "UTC" });
}

export interface DailyBar {
  day: string;
  value: number;
  /** Today: still being counted. */
  partial: boolean;
  /** The day counts toward the value shown above the bars (the selected range). */
  inRange: boolean;
}

const W = 140;
const H = 36;
const MAX_BAR = 10;

/** Column path with a 2 px rounded data end and a square base on the baseline. */
function columnPath(x: number, w: number, h: number): string {
  const y = H - h;
  const r = Math.min(2, w / 2, h);
  return `M${x},${H}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${H}Z`;
}

/**
 * A tile's tiny trend: one column per UTC day (measured values only, from GET /stats/daily). Days inside the selected range
 * are drawn in the metric's accent, the others as a light wash; a day with no activity is a hairline on the baseline (a
 * real zero). Each day has a hover tooltip; the summary is the image's accessible name and every value is also in the
 * "All ranges" daily table. `format` (optional) shows a value in full (e.g. money: "$5.00") instead of a count + noun.
 */
export function DailyBars({ bars, noun, summaryLabel, format }: { bars: DailyBar[]; noun: (n: number) => string; summaryLabel: string; format?: (n: number) => string }) {
  if (!bars.length) return null;
  const n = bars.length;
  const slot = W / n;
  const w = Math.min(MAX_BAR, Math.max(1.6, slot * 0.68));
  const max = Math.max(1, ...bars.map((b) => b.value));
  const total = bars.reduce((s, b) => s + b.value, 0);
  const peak = bars.reduce((best, b) => (b.value > best.value ? b : best), bars[0]!);
  const today = bars.at(-1)!;
  const show = (v: number) => (format ? format(v) : `${formatCount(v)} ${noun(v)}`);
  const amount = (v: number) => (format ? format(v) : formatCount(v));
  const summary =
    `${summaryLabel}, last ${n} UTC days: ${total === 0 ? "none recorded" : `${amount(total)} in all, most on ${utcDayLabel(peak.day, false)} (${amount(peak.value)})`}` +
    `${today.partial ? `; today so far ${amount(today.value)}` : ""}.`;
  return (
    <svg className="stat-bars" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary} preserveAspectRatio="none">
      <line className="stat-bars-base" x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} vectorEffect="non-scaling-stroke" />
      {bars.map((b, i) => {
        const x = i * slot + (slot - w) / 2;
        const h = b.value === 0 ? 0 : Math.max(2.5, (b.value / max) * (H - 3));
        const tip = `${utcDayLabel(b.day)}${b.partial ? " (today, so far)" : ""}: ${show(b.value)}`;
        return (
          <g key={b.day} className={`stat-bar${b.inRange ? " in-range" : ""}${b.value === 0 ? " zero" : ""}`} data-day={b.day}>
            <title>{tip}</title>
            <rect className="stat-bar-hit" x={i * slot} y="0" width={slot} height={H} />
            {h > 0 ? <path className="stat-bar-fill" d={columnPath(x, w, h)} /> : <rect className="stat-bar-zero" x={x} y={H - 1.5} width={w} height={1.5} />}
          </g>
        );
      })}
    </svg>
  );
}
